import { inRn, mString, pluralize } from 'lib/helpers/utility';

import { computeEndDate } from '../../../lib/helpers';

import InfoBox from './InfoBox';

const getFrequencyText = (repeatEvent, repeatEvery) => {
  const base = repeatEvent === 'weekly' ? 'week' : repeatEvent === 'yearly' ? 'year' : 'month';

  if (repeatEvery && repeatEvery > 1) {
    return `every ${repeatEvery} ${pluralize(base, repeatEvery)}`;
  }

  return repeatEvent || 'monthly';
};

const renderPaymentCount = (count, repeatEvent, repeatEvery) => {
  const freq = getFrequencyText(repeatEvent, repeatEvery);
  const isAdj = !repeatEvery || repeatEvery <= 1;

  if (isAdj) {
    return (
      <span className="whitespace-nowrap font-semibold">
        {count} {freq} {count === 1 ? 'payment' : 'payments'}
      </span>
    );
  }

  return (
    <span className="whitespace-nowrap font-semibold">
      {count} {count === 1 ? 'payment' : 'payments'} {freq}
    </span>
  );
};

const renderEndDate = (endDate) => {
  if (!endDate) return null;
  return (
    <>
      {' '}
      The last payment will be on <span className="whitespace-nowrap font-semibold">{endDate}</span>
      .
    </>
  );
};

const renderUpfrontPrefix = (upfrontCents, subject) => {
  if (!upfrontCents || upfrontCents <= 0) return null;
  return (
    <div className="font-semibold">
      {subject} will be charged {mString(upfrontCents)} upfront.
    </div>
  );
};

const InstallmentsInfo = ({ recurringPayment, totalAmount, subject = 'The patient' }) => {
  const {
    paymentType,
    repeatEvent,
    repeatEvery,
    startDate,
    numberOfPayments,
    amountPerPayment,
    upfrontPayment
  } = recurringPayment;
  const totalPayments = inRn(numberOfPayments, 1);
  const upfrontCents = inRn(upfrontPayment, 0);
  const effectiveTotal = Math.max(0, inRn(totalAmount, 0) - upfrontCents);
  const upfrontPrefix = renderUpfrontPrefix(upfrontCents, subject);
  const leadIn = upfrontPrefix ? (
    <>{upfrontPrefix}The remaining balance will be charged by </>
  ) : (
    `${subject} will be charged `
  );

  if (paymentType === 'number-of-payments' && totalPayments && effectiveTotal) {
    const totalCents = effectiveTotal;

    if (!totalCents || totalCents <= 0) {
      return null;
    }

    if (totalPayments === 1) {
      const endDate = computeEndDate(startDate, totalPayments, repeatEvent, repeatEvery);

      return (
        <InfoBox>
          {leadIn}a single payment of{' '}
          <span className="whitespace-nowrap font-semibold">{mString(totalCents)}</span>.
          {renderEndDate(endDate)}
        </InfoBox>
      );
    }

    const basePaymentCents = Math.floor(totalCents / totalPayments);
    const remainderCents = totalCents - basePaymentCents * totalPayments;
    const finalPaymentCents = basePaymentCents + remainderCents;
    const endDate = computeEndDate(startDate, totalPayments, repeatEvent, repeatEvery);

    if (remainderCents === 0) {
      return (
        <InfoBox>
          {leadIn}
          {renderPaymentCount(totalPayments, repeatEvent, repeatEvery)} of{' '}
          <span className="whitespace-nowrap font-semibold">{mString(basePaymentCents)}</span>, for
          a total of <span className="whitespace-nowrap font-semibold">{mString(totalCents)}</span>.
          {renderEndDate(endDate)}
        </InfoBox>
      );
    }

    return (
      <InfoBox>
        {leadIn}
        {renderPaymentCount(totalPayments - 1, repeatEvent, repeatEvery)} of{' '}
        <span className="whitespace-nowrap font-semibold">{mString(basePaymentCents)}</span>,
        followed by a final payment of{' '}
        <span className="whitespace-nowrap font-semibold">{mString(finalPaymentCents)}</span>, for a
        total of <span className="whitespace-nowrap font-semibold">{mString(totalCents)}</span>.
        {renderEndDate(endDate)}
      </InfoBox>
    );
  }

  if (paymentType === 'amount-per-payment' && amountPerPayment && effectiveTotal) {
    const perPaymentCents = Number(amountPerPayment);
    const totalCents = effectiveTotal;

    if (!Number.isFinite(perPaymentCents) || perPaymentCents <= 0) {
      return null;
    }

    const fullPayments = Math.floor(totalCents / perPaymentCents);
    const remainderCents = totalCents - fullPayments * perPaymentCents;
    const hasFinalPayment = remainderCents > 0;
    const totalPaymentsCount = fullPayments + (hasFinalPayment ? 1 : 0);

    const endDate = computeEndDate(startDate, totalPaymentsCount, repeatEvent, repeatEvery);

    if (fullPayments === 0 && hasFinalPayment) {
      return (
        <InfoBox>
          {leadIn}a single payment of{' '}
          <span className="whitespace-nowrap font-semibold">{mString(totalCents)}</span>.
          {renderEndDate(endDate)}
        </InfoBox>
      );
    }

    return (
      <InfoBox>
        {leadIn}
        {renderPaymentCount(fullPayments, repeatEvent, repeatEvery)} of{' '}
        <span className="whitespace-nowrap font-semibold">{mString(perPaymentCents)}</span>
        {hasFinalPayment && fullPayments > 0 && (
          <>
            , followed by a final payment of{' '}
            <span className="whitespace-nowrap font-semibold">{mString(remainderCents)}</span>
          </>
        )}
        , for a total of{' '}
        <span className="whitespace-nowrap font-semibold">{mString(totalCents)}</span>.
        {renderEndDate(endDate)}
      </InfoBox>
    );
  }

  return null;
};

export default InstallmentsInfo;
